import Print from '@mui/icons-material/Print'
import Send from '@mui/icons-material/Send'
import Download from '@mui/icons-material/Download'
import { Button, CircularProgress } from '@mui/material'
import { useRef } from 'react'
import UploadedEmrReportImagesViewer from 'src/mentor/components/PrescriptionMakingPage/printComponents/prescriptionImageRender/UploadedPrescrptionImagesViewer'
import ScreeningReportDisplay from 'src/mentor/components/PrescriptionMakingPage/screeningReportViewer/ScreeningReportDisplay'
import ReportSignatureBlock from 'src/mentor/components/PrescriptionMakingPage/printComponents/printComponents/ReportSignatureBlock'
import usePrintableScreeningReportActions from 'src/mentor/components/ScreeningReportMakingPage/printableReport/hooks/usePrintableScreeningReportActions'
import { PMPParent } from '../../PrescriptionMakingPage/PrescriptionMakingPageStyles'
import { PrintStyles } from './SectionCard'

// hide-on-print-only
export default function PrintableScreeningReportComponent({
  report,
  emrSectionDataList,
  emrSectionPrintSettings,
  isAlwaysPrinting = false,
  hideDownloadButton,
  setReport,
  templateData,
  headingText = 'Screening Report',
  hideHeader = false,
  hideHeadingText = false,
  setIsPrintingPastHistoryReports = () => {},
  reportContainerRef,
  screeningSections,
  hideSendToDoctorButton = false,
  sendReportToTriggeringPractitionerApiSignature = '/api/secure/mentor/emr-reports/send-to-triggering-practitioner',
  bookedEventInfo,
  doctorSpecializations = null, // Doctor specializations for fetching screening sections
  visitType = null, // Visit type ('new' or 'repeat') for fetching screening sections
  useDebugNumbering = false, // Only use "Question 1", "Question 2" for debugging in history drawer
  screeningForSpecializations = null, // Specializations saved with the report (for history drawer)
  emrSections = null, // EMR sections config (e.g. from useEmrSections for 'screening-report') for signature section
}) {
  // Internal ref for print if external ref not provided
  const internalPrintRef = useRef(null)
  const printContainerRef = reportContainerRef || internalPrintRef
  
  const { isPrinting, printScreening, saving, sendReportToTriggeringPractitioner, sendingReport, downloadScreeningReport, downloadingReport } =
    usePrintableScreeningReportActions({
      setIsPrintingPastHistoryReports,
      isAlwaysPrinting,
      sendReportToTriggeringPractitionerApiSignature,
      screeningReport: report,
      screeningReportContainerRef: printContainerRef,
      bookedEventInfo: bookedEventInfo || report,
    })

  function renderReport() {
    if (!!report?.uploadedImages && report?.uploadedImages?.length > 0) {
      return (
        <UploadedEmrReportImagesViewer
          emrReportData={report}
          setEmrReportData={setReport}
          dontUseSignedUrls
        />
      )
    }
    
    // Check if blank header space should be used (similar to prescription pattern)
    // Check templateData for blank header setting
    const shouldLeaveSpace = templateData?.header?.customHeaderBlank === true || 
                             templateData?.blankHeader === true ||
                             (isPrinting && templateData?.header?.customHeaderBlank === true)
    
    return (
      <div className={`${!isPrinting ? 'hide-on-print-only' : ''} w-100`}>
        <PMPParent ref={printContainerRef}>
          <ScreeningReportDisplay
            reportData={report}
            screeningSections={screeningSections}
            screeningReportTemplateData={templateData}
            bookedEventInfo={bookedEventInfo || report}
            emrSectionDataList={emrSectionDataList}
            emrSectionPrintSettings={emrSectionPrintSettings}
            mentorProfileData={{
              name: report?.mentorName,
              id: report?.mentorId,
              specializedCategories: doctorSpecializations || report?.doctorSpecializations || report?.mentorSpecializations || report?.specializations,
              specializations: doctorSpecializations || report?.doctorSpecializations || report?.mentorSpecializations || report?.specializations,
            }}
            bottomBorderBorderModifier="1px solid #000"
            doctorSpecializations={
              // Prioritize screeningForSpecializations from saved report, then fallback
              screeningForSpecializations ||
              doctorSpecializations || 
              report?.doctorSpecializations || 
              report?.mentorSpecializations || 
              report?.specializations
            }
            visitType={visitType || bookedEventInfo?.visitType || (bookedEventInfo?.patientMeetingSeqNumber > 1 ? 'repeat' : 'new')}
            useDebugNumbering={useDebugNumbering}
            screeningForSpecializations={screeningForSpecializations}
            leaveSpace={shouldLeaveSpace}
          />
          <ReportSignatureBlock
            emrSections={emrSections}
            reportData={report}
            reportType="screening-report"
          />
          {/* <>
            <ScreeningReportHeader
              screeningReportData={report}
              screenerName={report?.mentorName}
              templateData={templateData}
            />
            <div style={{ flex: 1, padding: '10px' }}>
              <ScreeningReportBodyFirstSection
                observationAndAdvice={{
                  advice: report?.advice || null,
                  observation: report?.observation || null,
                }}
                selectedComplaints={getSRInitialDataForSettingExistingSelectedComplaints(
                  report?.complaints,
                )}
                selectedDiagnosis={getSRInitialDataForSettingExistingSelectedDiagnosis(
                  report?.diagnosis,
                )}
                screeningSections={screeningSections}
                screeningQuestionAnswers={report?.screeningAnswers}
                // screeningQuestionAnswers={report?.screeningAnswers}
                reportData={report}
              />
              <ScreeningReportBody
                observationAndAdvice={{
                  advice: report?.advice || null,
                  observation: report?.observation || null,
                }}
                content={report?.content}
                suggestedBookingTherapy={report?.suggestedBookingTherapy}
              />
              <EmrSectionsListDisplay emrSectionDataList={emrSectionDataList} />
            </div>
          </> */}
        </PMPParent>
      </div>
    )
  }

  return (
    <>
      <PrintStyles />
      {!hideHeader && (
        <div className="d-flex align-items-center mb-2 mt-3 gap-2 w-100 hide-on-print-only" style={{ opacity: 0.85 }}>
          {!hideHeadingText && <u>{headingText}</u>}
          {!hideDownloadButton && (
            <>
              <Button
                variant="outlined"
                startIcon={<Download />}
                sx={{ 
                  padding: '4px 12px', 
                  minWidth: '0px',
                  fontSize: '12px',
                  textTransform: 'none',
                  borderColor: 'rgba(67, 56, 202, 0.3)',
                  color: 'rgba(67, 56, 202, 0.7)',
                  backgroundColor: 'rgba(67, 56, 202, 0.04)',
                  '&:hover': {
                    borderColor: 'rgba(67, 56, 202, 0.5)',
                    backgroundColor: 'rgba(67, 56, 202, 0.08)',
                    color: 'rgba(67, 56, 202, 0.9)',
                  },
                  '&:disabled': {
                    borderColor: 'rgba(67, 56, 202, 0.2)',
                    color: 'rgba(67, 56, 202, 0.4)',
                    backgroundColor: 'rgba(67, 56, 202, 0.02)',
                  },
                }}
                size="small"
                disabled={downloadingReport}
                onClick={() => {
                  downloadScreeningReport()
                }}
              >
                Download PDF {downloadingReport && <CircularProgress size="14px" sx={{ ml: 1 }} />}
              </Button>
              <Button
                variant="outlined"
                startIcon={<Print />}
                sx={{ 
                  padding: '4px 12px', 
                  minWidth: '0px',
                  fontSize: '12px',
                  textTransform: 'none',
                  borderColor: 'rgba(67, 56, 202, 0.3)',
                  color: 'rgba(67, 56, 202, 0.7)',
                  backgroundColor: 'rgba(67, 56, 202, 0.04)',
                  '&:hover': {
                    borderColor: 'rgba(67, 56, 202, 0.5)',
                    backgroundColor: 'rgba(67, 56, 202, 0.08)',
                    color: 'rgba(67, 56, 202, 0.9)',
                  },
                }}
                size="small"
                onClick={() => {
                  printScreening()
                }}
              >
                Print {saving && <CircularProgress size="14px" sx={{ ml: 1 }} />}
              </Button>
            </>
          )}
          {!hideSendToDoctorButton && (
            <Button
              variant="outlined"
              startIcon={<Send />}
              sx={{ 
                padding: '4px 12px', 
                minWidth: '0px',
                fontSize: '12px',
                textTransform: 'none',
                borderColor: 'rgba(67, 56, 202, 0.3)',
                color: 'rgba(67, 56, 202, 0.7)',
                backgroundColor: 'rgba(67, 56, 202, 0.04)',
                '&:hover': {
                  borderColor: 'rgba(67, 56, 202, 0.5)',
                  backgroundColor: 'rgba(67, 56, 202, 0.08)',
                  color: 'rgba(67, 56, 202, 0.9)',
                },
                '&:disabled': {
                  borderColor: 'rgba(67, 56, 202, 0.2)',
                  color: 'rgba(67, 56, 202, 0.4)',
                  backgroundColor: 'rgba(67, 56, 202, 0.02)',
                },
              }}
              size="small"
              disabled={sendingReport}
              onClick={() => {
                sendReportToTriggeringPractitioner()
              }}
            >
              Send To {report?.requestingDoctorName || 'Consulting Doctor'}{' '}
              {sendingReport && <CircularProgress size="14px" sx={{ ml: 1 }} />}
            </Button>
          )}
        </div>
      )}
      {renderReport()}
    </>
  )
}
